import React from 'react'
import styled from 'styled-components'
import GavelRoundedIcon            from '@mui/icons-material/GavelRounded'
import MenuBookRoundedIcon         from '@mui/icons-material/MenuBookRounded'
import AssessmentRoundedIcon       from '@mui/icons-material/AssessmentRounded'
import VerifiedUserRoundedIcon     from '@mui/icons-material/VerifiedUserRounded'
import PersonAddAltRoundedIcon     from '@mui/icons-material/PersonAddAltRounded'
import HistoryToggleOffRoundedIcon from '@mui/icons-material/HistoryToggleOffRounded'
import LaunchRoundedIcon           from '@mui/icons-material/LaunchRounded'
import HistoryRoundedIcon          from '@mui/icons-material/HistoryRounded'
import { Tooltip } from '@mui/material'

const BASIS_META = {
  statutory: {
    label: 'Statutory',
    colour: '#b00020',
    Icon: GavelRoundedIcon,
  },
  guidance: {
    label: 'Recommended (guidance)',
    colour: '#d97706',
    Icon: MenuBookRoundedIcon,
  },
  risk_assessment: {
    label: 'Risk-assessment-led',
    colour: '#5b6bd6',
    Icon: AssessmentRoundedIcon,
  },
}

const formatDate = iso => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const isPast = iso => {
  if (!iso) return false
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return false
  return d.getTime() < Date.now()
}

const HintTarget = styled.span` display: inline-flex; `
const Hint = ({ title, children, style }) => (
  <Tooltip
    title={title}
    arrow
    placement='top'
    enterDelay={200}
    slotProps={{
      tooltip: { sx: { maxWidth: 280, fontSize: '11.5px', lineHeight: 1.5, padding: '8px 11px', bgcolor: '#1d2433', fontWeight: 500 } },
      arrow: { sx: { color: '#1d2433' } },
    }}
  >
    <HintTarget style={style}>{children}</HintTarget>
  </Tooltip>
)

const ObligationProvenance = ({
  cadenceBasis,
  lastVerifiedAt,
  reviewDueAt,
  isSystem,
  citationUrl,
  regulatoryFramework,
  onHistory,
}) => {
  const basis = BASIS_META[cadenceBasis]
  const verifiedDate = formatDate(lastVerifiedAt)
  const reviewDue = isPast(reviewDueAt)

  return (
    <Row>
      {basis && (
        <Badge $colour={basis.colour}>
          <basis.Icon style={{ fontSize: 13 }} />
          <span>{basis.label}</span>
        </Badge>
      )}

      {isSystem === false ? (
        <CustomBadge>
          <PersonAddAltRoundedIcon style={{ fontSize: 13 }} />
          <span>Custom · added by your organisation</span>
        </CustomBadge>
      ) : verifiedDate ? (
        citationUrl ? (
          <VerifiedLink href={citationUrl} target='_blank' rel='noopener noreferrer'>
            <VerifiedUserRoundedIcon style={{ fontSize: 13 }} />
            <span>
              Verified {verifiedDate}
              {regulatoryFramework ? ` · ${regulatoryFramework}` : ''}
            </span>
            <LaunchRoundedIcon style={{ fontSize: 11 }} />
          </VerifiedLink>
        ) : (
          <VerifiedTag>
            <VerifiedUserRoundedIcon style={{ fontSize: 13 }} />
            <span>
              Verified {verifiedDate}
              {regulatoryFramework ? ` · ${regulatoryFramework}` : ''}
            </span>
          </VerifiedTag>
        )
      ) : null}

      {citationUrl && (isSystem === false || !verifiedDate) && (
        <VerifiedLink href={citationUrl} target='_blank' rel='noopener noreferrer'>
          <span>View source</span>
          <LaunchRoundedIcon style={{ fontSize: 11 }} />
        </VerifiedLink>
      )}

      {reviewDue && (
        <Hint title='Our last verification of this rule is older than its review interval. Currency is shown as at the verified date above.'>
          <ReviewDue>
            <HistoryToggleOffRoundedIcon style={{ fontSize: 13 }} />
            <span>Review due</span>
          </ReviewDue>
        </Hint>
      )}

      {onHistory && (
        <Hint title='See the full satisfaction history. Every status change, who made it, and when.' style={{ marginLeft: 'auto' }}>
          <HistoryPill type='button' onClick={onHistory}>
            <HistoryRoundedIcon style={{ fontSize: 12 }} />
            <span>History</span>
          </HistoryPill>
        </Hint>
      )}
    </Row>
  )
}

export default ObligationProvenance

const Row = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
`

const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px 3px 7px;
  border-radius: 999px;
  background: ${({ $colour }) => `${$colour}14`};
  color: ${({ $colour }) => $colour};
  border: 1px solid ${({ $colour }) => `${$colour}33`};
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.02em;
  white-space: nowrap;
`

const CustomBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px 3px 7px;
  border-radius: 999px;
  background: rgba(12, 18, 41, 0.05);
  color: ${({ theme }) => theme.colors.grey[800]};
  border: 1px solid #dcdce0;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
`

const VerifiedLink = styled.a`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px 3px 7px;
  border-radius: 999px;
  background: rgba(31, 140, 151, 0.08);
  color: #1f8c97;
  border: 1px solid rgba(31, 140, 151, 0.24);
  font-size: 10.5px;
  font-weight: 700;
  text-decoration: none;
  &:hover { background: rgba(31, 140, 151, 0.16); }
`

const VerifiedTag = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px 3px 7px;
  border-radius: 999px;
  background: rgba(31, 140, 151, 0.08);
  color: #1f8c97;
  border: 1px solid rgba(31, 140, 151, 0.24);
  font-size: 10.5px;
  font-weight: 700;
`

const ReviewDue = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px 3px 7px;
  border-radius: 999px;
  background: rgba(232, 154, 92, 0.12);
  color: #b5722f;
  border: 1px dashed rgba(232, 154, 92, 0.5);
  font-size: 10.5px;
  font-weight: 700;
  cursor: help;
`

const HistoryPill = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px 3px 7px;
  border-radius: 999px;
  background: rgba(12, 18, 41, 0.05);
  color: #6e6e78;
  border: 1px solid #dcdce0;
  font-size: 10.5px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: rgba(12, 18, 41, 0.1); color: #2a2a35; }
`
